// src/features/dashboard/components/TeamActiveHoursTable.tsx

import {
  useEffect,
  useMemo,
} from 'react';

import {
  useManagerDate,
  formatManagerDate,
} from '../context/ManagerDateContext';

import {
  useTeamTable,
} from '../hooks/useTeamTable';


/* ============================================================
 * TYPES
 * ============================================================ */

type Leader = {
  employeeCode: string;
  employeeName: string;
};

type Location = {
  geo: string;
};

type Props = {
  onOpenPopup: (
    employeeName: string,
    selectedTime: string,
    employeeCode: string,
    loggedDate: string,
  ) => void;

  ManagerEmployeeCode?: string;

  selectedLeader?: string;

  searchTerm?: string;

  selectedLocation?: string;

  onLeadersLoaded?: (
    leaders: Leader[],
  ) => void;

  onLocationsLoaded?: (
    locations: Location[],
  ) => void;
};


/* ============================================================
 * HOURS DATA
 * ============================================================ */

type HoursDataItem = {
  employeeId?: string | number;

  employeeCode?: string;

  employeeName?: string;

  name?: string;

  designation?: string;

  role?: string;

  geo?: string;

  loggedDate?: string;

  date?: string;

  dayDate?: string;

  displayValue?: string;

  totalMinutes?: number;

  avgMinutesPerDay?: number;
};


/* ============================================================
 * API EMPLOYEE
 * ============================================================ */

type ApiEmployee = {
  employeeId?: string | number;

  employeeCode?: string;

  employeeName?: string;

  name?: string;

  designation?: string;

  role?: string;

  geo?: string;

  initials?: string;

  color?: string;

  total?: string;

  avg?: string;

  hoursData?: HoursDataItem[];

  loggedDate?: string;

  date?: string;

  dayDate?: string;

  displayValue?: string;

  totalMinutes?: number;

  avgMinutesPerDay?: number;
};


/* ============================================================
 * EMPLOYEE ROW
 * ============================================================ */

type EmployeeRow = {
  employeeId?: string | number;

  employeeCode: string;

  employeeName: string;

  designation: string;

  geo: string;

  initials: string;

  color: string;

  /*
   * ALWAYS an array.
   */
  hoursData: HoursDataItem[];

  apiTotal?: string;

  apiAverage?: string;
};


/* ============================================================
 * DAY
 * ============================================================ */

type TableDay = {
  loggedDate?: string;

  date?: string;

  dayDate?: string;
};


/* ============================================================
 * TEAM AVERAGE
 * ============================================================ */

type TeamAverage = {
  loggedDate?: string;

  date?: string;

  dayDate?: string;

  teamAverageDisplay?: string;

  displayValue?: string;

  average?: string;

  avg?: string;
};


/* ============================================================
 * RESPONSE
 * ============================================================ */

type TeamTableResponse = {
  EMPLOYEES?: ApiEmployee[];

  DAYS?: TableDay[];

  TEAM_AVERAGES?: TeamAverage[];

  employees?: ApiEmployee[];

  days?: TableDay[];

  teamAverages?: TeamAverage[];
};


/* ============================================================
 * TABLE WIDTH
 * ============================================================ */

const MEMBER_WIDTH = 294;

const DAY_WIDTH = 82;

const TOTAL_WIDTH = 130;


/* ============================================================
 * AVATAR COLORS
 * ============================================================ */

const AVATAR_COLORS = [
  'bg-[#3B82F6]',
  'bg-[#06B6D4]',
  'bg-[#10B981]',
  'bg-[#8B5CF6]',
  'bg-[#F59E0B]',
  'bg-[#EC4899]',
  'bg-[#14B8A6]',
];


/* ============================================================
 * NORMALIZE DATE
 * ============================================================ */

const normalizeDate = (
  value?: unknown,
): string => {

  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  const text =
    String(value).trim();

  if (!text) {
    return '';
  }

  /*
   * ISO:
   * 2026-08-27T00:00:00
   */
  if (text.includes('T')) {
    return text.split('T')[0];
  }

  /*
   * SQL:
   * 2026-08-27 00:00:00
   */
  if (text.includes(' ')) {
    return text.split(' ')[0];
  }

  return text.slice(0, 10);
};


/* ============================================================
 * GET RECORD DATE
 * ============================================================ */

const getRecordDate = (
  record: HoursDataItem,
): string => {

  return normalizeDate(
    record.loggedDate ??
      record.date ??
      record.dayDate,
  );
};


/* ============================================================
 * GET DAY DATE
 * ============================================================ */

const getDayDate = (
  day: TableDay,
): string => {

  return normalizeDate(
    day.loggedDate ??
      day.date ??
      day.dayDate,
  );
};


/* ============================================================
 * GET TEAM AVERAGE DATE
 * ============================================================ */

const getTeamAverageDate = (
  item: TeamAverage,
): string => {

  return normalizeDate(
    item.loggedDate ??
      item.date ??
      item.dayDate,
  );
};


/* ============================================================
 * GET DISPLAY VALUE
 * ============================================================ */

const getDisplayValue = (
  record?: HoursDataItem,
): string => {

  if (!record) {
    return '-';
  }

  if (
    record.displayValue ===
      undefined ||
    record.displayValue === null
  ) {
    return '-';
  }

  const value =
    String(
      record.displayValue,
    ).trim();

  return value || '-';
};


/* ============================================================
 * TIME TO MINUTES
 * ============================================================ */

const timeToMinutes = (
  value?: string | number | null,
): number => {

  if (
    value === undefined ||
    value === null
  ) {
    return 0;
  }

  const text =
    String(value).trim();

  if (
    !text ||
    text === '-'
  ) {
    return 0;
  }

  const parts =
    text.split(':');

  if (
    parts.length <
    2
  ) {
    return 0;
  }

  const hours =
    Number(parts[0]);

  const minutes =
    Number(parts[1]);

  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes)
  ) {
    return 0;
  }

  return (
    hours * 60 +
    minutes
  );
};


/* ============================================================
 * MINUTES TO HH:MM
 * ============================================================ */

const minutesToTime = (
  minutes: number,
): string => {

  if (
    !Number.isFinite(minutes) ||
    minutes < 0
  ) {
    return '00:00';
  }

  const rounded =
    Math.round(minutes);

  const hours =
    Math.floor(
      rounded / 60,
    );

  const mins =
    rounded % 60;

  return (
    `${String(hours).padStart(2, '0')}:` +
    `${String(mins).padStart(2, '0')}`
  );
};


/* ============================================================
 * INITIALS
 * ============================================================ */

const getInitials = (
  name: string,
): string => {

  const parts =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean);

  if (
    parts.length === 0
  ) {
    return '--';
  }

  return parts
    .slice(0, 3)
    .map(
      (part) =>
        part.charAt(0),
    )
    .join('')
    .toUpperCase();
};


/* ============================================================
 * HEADER DATE
 * ============================================================ */

const getDayHeader = (
  date: string,
) => {

  if (!date) {
    return {
      day: '',
      weekday: '',
    };
  }

  const parsed =
    new Date(
      `${date}T00:00:00`,
    );

  if (
    Number.isNaN(
      parsed.getTime(),
    )
  ) {
    return {
      day: date.slice(8, 10),
      weekday: '',
    };
  }

  return {
    day:
      String(
        parsed.getDate(),
      ),

    weekday:
      parsed
        .toLocaleDateString(
          'en-US',
          {
            weekday: 'short',
          },
        )
        .toUpperCase(),
  };
};


/* ============================================================
 * CELL COLOR
 * ============================================================ */

const getCellClass = (
  value: string,
): string => {

  if (
    !value ||
    value === '-'
  ) {
    return `
      bg-white
      text-[#101828]
    `;
  }

  const minutes =
    timeToMinutes(value);

  if (
    minutes >= 480
  ) {
    return `
      bg-[#20C997]
      text-white
    `;
  }

  if (
    minutes >= 360
  ) {
    return `
      bg-[#B7E8D4]
      text-[#101828]
    `;
  }

  if (
    minutes >= 300
  ) {
    return `
      bg-[#F8DF80]
      text-[#101828]
    `;
  }

  return `
    bg-white
    text-[#101828]
  `;
};


/* ============================================================
 * NORMALIZE EMPLOYEES
 * ============================================================ */

const normalizeEmployees = (
  response: TeamTableResponse,
): EmployeeRow[] => {

  /*
   * Support BOTH:
   *
   * EMPLOYEES
   * employees
   */

  const source =
    Array.isArray(
      response.EMPLOYEES,
    )
      ? response.EMPLOYEES
      : Array.isArray(
          response.employees,
        )
        ? response.employees
        : [];


  const employeeMap =
    new Map<
      string,
      EmployeeRow
    >();


  source.forEach(
    (
      item,
      index,
    ) => {

      const employeeCode =
        String(
          item.employeeCode ??
            '',
        ).trim();


      if (!employeeCode) {
        return;
      }


      const employeeName =
        String(
          item.employeeName ??
            item.name ??
            '',
        ).trim() ||
        '-';


      let employee =
        employeeMap.get(
          employeeCode,
        );


      /*
       * CREATE EMPLOYEE
       */

      if (!employee) {

        employee = {
          employeeId:
            item.employeeId,

          employeeCode,

          employeeName,

          designation:
            String(
              item.designation ??
                item.role ??
                '',
            ).trim(),

          geo:
            String(
              item.geo ??
                '',
            ).trim(),

          initials:
            item.initials &&
            item.initials.trim()
              ? item.initials
                  .trim()
                  .toUpperCase()
              : getInitials(
                  employeeName,
                ),

          color:
            item.color ??
            AVATAR_COLORS[
              index %
                AVATAR_COLORS.length
            ],

          hoursData: [],

          apiTotal:
            item.total,

          apiAverage:
            item.avg,
        };


        employeeMap.set(
          employeeCode,
          employee,
        );
      }


      /*
       * ADD hoursData
       */

      if (
        Array.isArray(
          item.hoursData,
        )
      ) {

        item.hoursData.forEach(
          (record) => {

            const date =
              getRecordDate(
                record,
              );


            if (!date) {
              return;
            }


            const exists =
              employee.hoursData.some(
                (existing) =>
                  getRecordDate(
                    existing,
                  ) === date,
              );


            if (!exists) {

              employee.hoursData.push(
                {
                  ...record,
                },
              );

            }

          },
        );
      }


      /*
       * SUPPORT FLAT DAILY RESPONSE
       */

      const flatDate =
        item.loggedDate ??
        item.date ??
        item.dayDate;


      if (
        flatDate &&
        item.displayValue !==
          undefined
      ) {

        const record:
          HoursDataItem = {

          employeeId:
            item.employeeId,

          employeeCode:
            item.employeeCode,

          employeeName:
            item.employeeName ??
            item.name,

          designation:
            item.designation ??
            item.role,

          geo:
            item.geo,

          loggedDate:
            item.loggedDate,

          date:
            item.date,

          dayDate:
            item.dayDate,

          displayValue:
            item.displayValue,

          totalMinutes:
            item.totalMinutes,

          avgMinutesPerDay:
            item.avgMinutesPerDay,
        };


        const date =
          getRecordDate(
            record,
          );


        const exists =
          employee.hoursData.some(
            (existing) =>
              getRecordDate(
                existing,
              ) === date,
          );


        if (
          date &&
          !exists
        ) {

          employee.hoursData.push(
            record,
          );

        }
      }

    },
  );


  return Array.from(
    employeeMap.values(),
  );
};


/* ============================================================
 * BUILD DAYS
 *
 * IMPORTANT:
 *
 * DAYS + EMPLOYEE hoursData
 *
 * This prevents dates from disappearing.
 * ============================================================ */

const buildDays = (
  responseDays: TableDay[],
  employees: EmployeeRow[],
): string[] => {

  const dateSet =
    new Set<string>();


  /*
   * API DAYS
   */

  responseDays.forEach(
    (day) => {

      const date =
        getDayDate(day);

      if (date) {
        dateSet.add(date);
      }
    },
  );


  /*
   * EMPLOYEE DAILY DATA
   */

  employees.forEach(
    (employee) => {

      employee.hoursData.forEach(
        (record) => {

          const date =
            getRecordDate(
              record,
            );

          if (date) {
            dateSet.add(date);
          }
        },
      );

    },
  );


  return Array.from(
    dateSet,
  ).sort(
    (
      first,
      second,
    ) =>
      first.localeCompare(
        second,
      ),
  );
};


/* ============================================================
 * FIND DAILY RECORD
 * ============================================================ */

const findDailyRecord = (
  employee: EmployeeRow,
  date: string,
): HoursDataItem | undefined => {

  return employee.hoursData.find(
    (record) =>
      getRecordDate(
        record,
      ) === date,
  );
};


/* ============================================================
 * EMPLOYEE TOTAL
 * ============================================================ */

const calculateEmployeeTotal = (
  employee: EmployeeRow,
): string => {

  /*
   * If API supplied total, use it.
   */

  if (
    employee.apiTotal &&
    employee.apiTotal.trim()
  ) {

    return employee.apiTotal
      .replace(
        '/day',
        '',
      )
      .trim();
  }


  let total = 0;

  let found = false;


  employee.hoursData.forEach(
    (record) => {

      const value =
        getDisplayValue(
          record,
        );


      if (
        value === '-'
      ) {
        return;
      }


      total +=
        timeToMinutes(
          value,
        );

      found = true;
    },
  );


  if (!found) {
    return '-';
  }


  return minutesToTime(
    total,
  );
};


/* ============================================================
 * EMPLOYEE AVERAGE
 * ============================================================ */

const calculateEmployeeAverage = (
  employee: EmployeeRow,
): string => {

  /*
   * API average has priority.
   */

  if (
    employee.apiAverage &&
    employee.apiAverage.trim()
  ) {

    return employee.apiAverage
      .replace(
        '/day',
        '',
      )
      .trim();
  }


  const values =
    employee.hoursData
      .map(
        (record) =>
          getDisplayValue(
            record,
          ),
      )
      .filter(
        (value) =>
          value !== '-',
      );


  if (
    values.length === 0
  ) {
    return '-';
  }


  const total =
    values.reduce(
      (
        sum,
        value,
      ) =>
        sum +
        timeToMinutes(
          value,
        ),
      0,
    );


  return minutesToTime(
    total /
      values.length,
  );
};


/* ============================================================
 * GET TEAM AVERAGE DISPLAY
 * ============================================================ */

const getTeamAverageDisplay = (
  item: TeamAverage,
): string => {

  const value =
    item.teamAverageDisplay ??
    item.displayValue ??
    item.average ??
    item.avg ??
    '';


  const result =
    String(value).trim();


  return result || '-';
};


/* ============================================================
 * CALCULATE TEAM AVERAGE FROM EMPLOYEES
 *
 * THIS IS THE IMPORTANT FIX.
 *
 * If API TEAM_AVERAGES doesn't contain a value for a date,
 * calculate it from the employee daily values.
 * ============================================================ */

const calculateDailyTeamAverage = (
  employees: EmployeeRow[],
  date: string,
): string => {

  const values: number[] = [];


  employees.forEach(
    (employee) => {

      const record =
        findDailyRecord(
          employee,
          date,
        );


      if (!record) {
        return;
      }


      const value =
        getDisplayValue(
          record,
        );


      if (
        value === '-'
      ) {
        return;
      }


      const minutes =
        timeToMinutes(
          value,
        );


      if (
        Number.isFinite(
          minutes,
        )
      ) {

        values.push(
          minutes,
        );

      }

    },
  );


  if (
    values.length === 0
  ) {
    return '-';
  }


  const total =
    values.reduce(
      (
        sum,
        value,
      ) =>
        sum + value,
      0,
    );


  return minutesToTime(
    total /
      values.length,
  );
};


/* ============================================================
 * COMPONENT
 * ============================================================ */

const TeamActiveHoursTable = ({
  onOpenPopup,
  ManagerEmployeeCode,
  selectedLeader = '',
  searchTerm = '',
  selectedLocation = '',
  onLeadersLoaded,
  onLocationsLoaded,
}: Props) => {

  /* ==========================================================
   * DATE RANGE
   * ========================================================== */

  const {
  fromDate,
  toDate,
} =
  useManagerDate();


  /* ==========================================================
   * PAYLOAD
   * ========================================================== */

const payload =
  useMemo(
    () => {

      const managerCode =
        String(
          ManagerEmployeeCode ??
            '',
        ).trim();

      const apiFromDate =
        formatManagerDate(
          fromDate,
        );

      const apiToDate =
        formatManagerDate(
          toDate,
        );

      const nextPayload = {
        managerEmployeeCode:
          managerCode,

        fromDate:
          apiFromDate,

        toDate:
          apiToDate,

        teamLeadEmployeeCode:
          selectedLeader?.trim()
            ? selectedLeader.trim()
            : null,

        geo:
          selectedLocation?.trim()
            ? selectedLocation.trim()
            : null,

        searchText:
          searchTerm?.trim()
            ? searchTerm.trim()
            : null,
      };

      console.log(
        '========== TEAM TABLE DATE FILTER =========='
      );

      console.log(
        'Selected From Date:',
        fromDate,
      );

      console.log(
        'Selected To Date:',
        toDate,
      );

      console.log(
        'API From Date:',
        apiFromDate,
      );

      console.log(
        'API To Date:',
        apiToDate,
      );

      console.log(
        'Final Team Table Payload:',
        nextPayload,
      );

      console.log(
        '============================================'
      );

      return nextPayload;
    },
    [
      ManagerEmployeeCode,
      fromDate,
      toDate,
      selectedLeader,
      selectedLocation,
      searchTerm,
    ],
  );

  /* ==========================================================
   * API
   * ========================================================== */

  const {
    data,
    loading,
    error,
  } =
    useTeamTable(
      payload,
    );


  /* ==========================================================
   * RESPONSE
   * ========================================================== */

  const response =
    (data ?? {}) as TeamTableResponse;


  /* ==========================================================
   * EMPLOYEES
   * ========================================================== */

  const employees =
    useMemo(
      () =>
        normalizeEmployees(
          response,
        ),
      [
        response,
      ],
    );


  /* ==========================================================
   * TEAM AVERAGES
   *
   * Support uppercase + lowercase API.
   * ========================================================== */

  const teamAverages =
    useMemo(
      () => {

        if (
          Array.isArray(
            response.TEAM_AVERAGES,
          )
        ) {

          return response.TEAM_AVERAGES;

        }


        if (
          Array.isArray(
            response.teamAverages,
          )
        ) {

          return response.teamAverages;

        }


        return [];

      },
      [
        response.TEAM_AVERAGES,
        response.teamAverages,
      ],
    );


  /* ==========================================================
   * DAYS
   *
   * Keep dates with a positive team average. If the API supplies
   * 00:00 for a date, do not render that date column.
   * ========================================================== */

  const responseDays =
    Array.isArray(response.DAYS)
      ? response.DAYS
      : Array.isArray(response.days)
        ? response.days
        : [];

  const days = useMemo(() => {
    const candidateDays = buildDays(responseDays, employees);

    const averageByDate = new Map<string, string>();

    teamAverages.forEach((item) => {
      const date = getTeamAverageDate(item);
      if (date) {
        averageByDate.set(date, getTeamAverageDisplay(item));
      }
    });

    return candidateDays.filter((date) => {
      const average = averageByDate.get(date);

      // If an explicit team average exists, only show the column
      // when its value is greater than zero.
      if (average !== undefined) {
        return average !== '-' && timeToMinutes(average) > 0;
      }

      // If the API did not return an average for this date, fall
      // back to employee data rather than hiding valid activity.
      return employees.some((employee) =>
        employee.hoursData.some((record) => {
          if (getRecordDate(record) !== date) return false;
          const value = getDisplayValue(record);
          return value !== '-' && timeToMinutes(value) > 0;
        }),
      );
    });
  }, [responseDays, employees, teamAverages]);

  /* ==========================================================
   * TEAM AVERAGE MAP
   * ========================================================== */

  const teamAverageMap =
    useMemo(
      () => {

        const map =
          new Map<
            string,
            string
          >();


        /*
         * FIRST:
         * Use API values.
         */

        teamAverages.forEach(
          (item) => {

            const date =
              getTeamAverageDate(
                item,
              );


            const value =
              getTeamAverageDisplay(
                item,
              );


            if (
              date &&
              value !== '-'
            ) {

              map.set(
                date,
                value,
              );

            }

          },
        );


        /*
         * SECOND:
         * Fill missing dates from employee data.
         *
         * Existing API values are NEVER overwritten.
         */

        days.forEach(
          (date) => {

            if (
              map.has(date)
            ) {
              return;
            }


            const calculated =
              calculateDailyTeamAverage(
                employees,
                date,
              );


            if (
              calculated !== '-'
            ) {

              map.set(
                date,
                calculated,
              );

            }

          },
        );


        return map;

      },
      [
        teamAverages,
        days,
        employees,
      ],
    );


  /* ==========================================================
   * TEAM TOTAL FOOTER
   *
   * Average of all visible daily team averages.
   *
   * Example:
   * 17:03, 07:42, 05:31...
   * -> final 08:08
   * ========================================================== */

  const footerTotal =
    useMemo(
      () => {

        const values =
          days
            .map(
              (date) =>
                teamAverageMap.get(
                  date,
                ) ?? '-',
            )
            .filter(
              (value) =>
                value !== '-',
            );


        if (
          values.length === 0
        ) {
          return '-';
        }


        const total =
          values.reduce(
            (
              sum,
              value,
            ) =>
              sum +
              timeToMinutes(
                value,
              ),
            0,
          );


        return minutesToTime(
          total /
            values.length,
        );

      },
      [
        days,
        teamAverageMap,
      ],
    );


  /* ==========================================================
   * LEADERS
   * ========================================================== */

  useEffect(() => {

    if (
      !onLeadersLoaded
    ) {
      return;
    }

    /*
     * Once a leader is selected, the API response is already
     * filtered to that leader's team. Do not rebuild the
     * dropdown from the filtered table rows because that would
     * replace the complete leader list with the current result.
     *
     * The parent should load the complete active/permanent
     * leader list from the dedicated team-leader API.
     */
    if (selectedLeader) {
      return;
    }


    const unique =
      new Map<
        string,
        Leader
      >();


    employees.forEach(
      (employee) => {

        if (
          !unique.has(
            employee.employeeCode,
          )
        ) {

          unique.set(
            employee.employeeCode,
            {
              employeeCode:
                employee.employeeCode,

              employeeName:
                employee.employeeName,
            },
          );

        }

      },
    );


    onLeadersLoaded(
      Array.from(
        unique.values(),
      ),
    );

  }, [
    employees,
    selectedLeader,
    onLeadersLoaded,
  ]);


  /* ==========================================================
   * LOCATIONS
   * ========================================================== */

  useEffect(() => {

    if (
      !onLocationsLoaded
    ) {
      return;
    }


    const unique =
      new Map<
        string,
        Location
      >();


    employees.forEach(
      (employee) => {

        const geo =
          employee.geo.trim();


        if (
          geo &&
          !unique.has(geo)
        ) {

          unique.set(
            geo,
            {
              geo,
            },
          );

        }

      },
    );


    onLocationsLoaded(
      Array.from(
        unique.values(),
      ),
    );

  }, [
    employees,
    onLocationsLoaded,
  ]);


  /* ==========================================================
   * FILTER
   * ========================================================== */

  const filteredEmployees =
    useMemo(
      () => {

        const normalizedSearch =
          searchTerm
            .trim()
            .toLowerCase();


        /*
         * Team-leader, location and search filtering are now
         * performed by the API.
         *
         * Do NOT filter the returned rows again here.
         * Doing so can remove valid rows returned by the
         * selected team-leader API request.
         */
        return employees;

      },
      [
        employees,
      ],
    );


  /* ==========================================================
   * LOADING
   * ========================================================== */

  if (loading) {

    return (
      <div
        className="
          flex
          min-h-[160px]
          w-full
          items-center
          justify-center
          text-[12px]
          text-[#667085]
        "
      >
        Loading team data...
      </div>
    );

  }


  /* ==========================================================
   * ERROR
   * ========================================================== */

  if (error) {

    return (
      <div
        className="
          flex
          min-h-[160px]
          w-full
          items-center
          justify-center
          p-6
          text-center
          text-[12px]
          text-red-500
        "
      >
        {String(error)}
      </div>
    );

  }


  /* ==========================================================
   * TABLE WIDTH
   *
   * ONE MEMBER COLUMN
   * + ALL DAY COLUMNS
   * + ONE TOTAL COLUMN
   * ========================================================== */

  const tableWidth =
    MEMBER_WIDTH +
    days.length *
      DAY_WIDTH +
    TOTAL_WIDTH;


  /* ==========================================================
   * RENDER
   * ========================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        max-w-full
        overflow-hidden
        rounded-b-[18px]
        border-t
        border-[#E5E7EB]
        bg-white
      "
    >

      <div
        className="
          w-full
          max-w-full
          overflow-x-auto
          overflow-y-hidden
        "
      >

        <table
          className="
            table-fixed
            border-collapse
          "
          style={{
            width:
              `${tableWidth}px`,

            minWidth:
              `${tableWidth}px`,
          }}
        >

          {/* ==================================================
              EXACT COLUMN STRUCTURE
          ================================================== */}

          <colgroup>

            <col
              style={{
                width:
                  `${MEMBER_WIDTH}px`,
              }}
            />

            {days.map(
              (
                date,
                index,
              ) => (
                <col
                  key={
                    `col-${date}-${index}`
                  }
                  style={{
                    width:
                      `${DAY_WIDTH}px`,
                  }}
                />
              ),
            )}

            {/* FINAL COLUMN */}

            <col
              style={{
                width:
                  `${TOTAL_WIDTH}px`,
              }}
            />

          </colgroup>


          {/* ==================================================
              HEADER
          ================================================== */}

          <thead>

            <tr
              className="
                h-[40px]
                border-b
                border-[#D9DEE7]
                bg-[#F3F4F6]
              "
            >

              {/* TEAM MEMBER */}

              <th
                className="
                  sticky
                  left-0
                  z-40
                  border-r
                  border-[#D9DEE7]
                  bg-[#F3F4F6]
                  px-[12px]
                  text-left
                  shadow-[2px_0_4px_rgba(16,24,40,0.08)]
                "
              >

                <span
                  className="
                    text-[9px]
                    font-bold
                    uppercase
                    text-[#002B5B]
                  "
                >
                  TEAM MEMBER
                </span>

              </th>


              {/* DAYS */}

              {days.map(
                (
                  date,
                  index,
                ) => {

                  const header =
                    getDayHeader(
                      date,
                    );


                  return (
                    <th
                      key={
                        `header-${date}-${index}`
                      }
                      className="
                        border-r
                        border-[#D9DEE7]
                        p-0
                        text-center
                      "
                    >

                      <div
                        className="
                          flex
                          h-[40px]
                          flex-col
                          items-center
                          justify-center
                        "
                      >

                        <span
                          className="
                            text-[12px]
                            font-bold
                            leading-[13px]
                            text-[#002B5B]
                          "
                        >
                          {
                            header.day
                          }
                        </span>

                        <span
                          className="
                            text-[8px]
                            font-semibold
                            leading-[10px]
                            text-[#667085]
                          "
                        >
                          {
                            header.weekday
                          }
                        </span>

                      </div>

                    </th>
                  );

                },
              )}


              {/* =================================================
                  30-DAY TOTAL
              ================================================= */}

              <th
                className="
                  sticky
                  right-0
                  z-40
                  bg-[#0B2852]
                  p-0
                  text-center
                  shadow-[-2px_0_4px_rgba(16,24,40,0.08)]
                "
              >

                <span
                  className="
                    text-[9px]
                    font-bold
                    uppercase
                    text-white
                  "
                >
                  30-DAY TOTAL
                </span>

              </th>

            </tr>

          </thead>


          {/* ==================================================
              BODY
          ================================================== */}

          <tbody>

            {filteredEmployees.length >
            0 ? (

              filteredEmployees.map(
                (
                  employee,
                  employeeIndex,
                ) => {

                  /*
                   * NEVER undefined.
                   */

                  const records =
                    Array.isArray(
                      employee.hoursData,
                    )
                      ? employee.hoursData
                      : [];


                  const total =
                    calculateEmployeeTotal(
                      employee,
                    );


                  const average =
                    calculateEmployeeAverage(
                      employee,
                    );


                  return (
                    <tr
                      key={
                        `${employee.employeeCode}-${employeeIndex}`
                      }
                      className="
                        h-[58px]
                        border-b
                        border-[#D9DEE7]
                      "
                    >

                      {/* ======================================
                          TEAM MEMBER
                      ====================================== */}

                      <td
                        className="
                          sticky
                          left-0
                          z-30
                          border-r
                          border-[#D9DEE7]
                          bg-white
                          p-0
                          shadow-[2px_0_4px_rgba(16,24,40,0.08)]
                        "
                      >

                        <div
                          className="
                            flex
                            h-[58px]
                            min-w-0
                            items-center
                            gap-[10px]
                            px-[12px]
                          "
                        >

                          <div
                            className={`
                              flex
                              h-[32px]
                              w-[32px]
                              shrink-0
                              items-center
                              justify-center
                              rounded-full
                              text-[9px]
                              font-bold
                              text-white
                              ${employee.color}
                            `}
                          >
                            {
                              employee.initials
                            }
                          </div>


                          <div
                            className="
                              min-w-0
                              flex-1
                            "
                          >

                            <div
                              className="
                                truncate
                                text-[12px]
                                font-semibold
                                leading-[16px]
                                text-[#101828]
                              "
                              title={
                                employee.employeeName
                              }
                            >
                              {
                                employee.employeeName
                              }
                            </div>


                            <div
                              className="
                                mt-[2px]
                                truncate
                                text-[8px]
                                font-medium
                                uppercase
                                leading-[10px]
                                text-[#667085]
                              "
                              title={
                                employee.geo
                                  ? `${employee.designation} · ${employee.geo}`
                                  : employee.designation
                              }
                            >

                              {
                                employee.designation
                              }

                              {
                                employee.geo
                                  ? ` · ${employee.geo}`
                                  : ''
                              }

                            </div>

                          </div>

                        </div>

                      </td>


                      {/* ======================================
                          DAILY DATA
                      ====================================== */}

                      {days.map(
                        (
                          date,
                          dayIndex,
                        ) => {

                          const record =
                            findDailyRecord(
                              employee,
                              date,
                            );


                          const value =
                            getDisplayValue(
                              record,
                            );


                          const isEmpty =
                            value === '-';


                          return (
                            <td
                              key={
                                `${employee.employeeCode}-${date}-${dayIndex}`
                              }
                              className={`
                                border-r
                                border-[#D9DEE7]
                                p-0
                                text-center
                                ${getCellClass(
                                  value,
                                )}
                                ${
                                  isEmpty
                                    ? ''
                                    : 'cursor-pointer'
                                }
                              `}
                              onClick={() => {

                                if (
                                  isEmpty
                                ) {
                                  return;
                                }


                                onOpenPopup(
                                  employee.employeeName,
                                  value,
                                  employee.employeeCode,
                                  date,
                                );

                              }}
                            >

                              <span
                                className="
                                  text-[9px]
                                  font-semibold
                                  leading-[12px]
                                "
                              >
                                {
                                  value
                                }
                              </span>

                            </td>
                          );

                        },
                      )}


                      {/* ======================================
                          30-DAY TOTAL
                          ALWAYS LAST
                      ====================================== */}

                      <td
                        className="
                          sticky
                          right-0
                          z-30
                          bg-white
                          p-0
                          text-center
                          shadow-[-2px_0_4px_rgba(16,24,40,0.08)]
                        "
                      >

                        <div
                          className="
                            flex
                            h-[58px]
                            flex-col
                            items-center
                            justify-center
                          "
                        >

                          <span
                            className="
                              text-[15px]
                              font-bold
                              leading-[18px]
                              text-[#002B5B]
                            "
                          >
                            {
                              total
                            }
                          </span>


                          <span
                            className="
                              mt-[2px]
                              text-[8px]
                              font-medium
                              leading-[11px]
                              text-[#667085]
                            "
                          >
                            avg {
                              average
                            }/day
                          </span>

                        </div>

                      </td>

                    </tr>
                  );

                },
              )

            ) : (

              <tr>

                <td
                  colSpan={
                    days.length + 2
                  }
                  className="
                    h-[70px]
                    text-center
                    text-[12px]
                    text-[#667085]
                  "
                >
                  No team member found
                </td>

              </tr>

            )}

          </tbody>


          {/* ==================================================
              TEAM AVERAGE FOOTER
          ================================================== */}

          <tfoot>

            <tr
              className="
                h-[34px]
                bg-[#0B2852]
              "
            >

              {/* TEAM AVERAGE LABEL */}

              <td
                className="
                  sticky
                  left-0
                  z-40
                  border-r
                  border-[#27456D]
                  bg-[#0B2852]
                  px-[12px]
                  shadow-[2px_0_4px_rgba(16,24,40,0.12)]
                "
              >

                <span
                  className="
                    text-[9px]
                    font-bold
                    uppercase
                    text-white
                  "
                >
                  TEAM AVERAGE
                </span>

              </td>


              {/* ==============================================
                  ONE FOOTER CELL FOR EVERY DAY

                  IMPORTANT:
                  This is exactly the same number of cells
                  as the header/body date columns.
              ============================================== */}

              {days.map(
                (
                  date,
                  index,
                ) => {

                  const value =
                    teamAverageMap.get(
                      date,
                    ) ??
                    '-';


                  return (
                    <td
                      key={
                        `footer-${date}-${index}`
                      }
                      className="
                        h-[34px]
                        border-r
                        border-[#27456D]
                        bg-[#0B2852]
                        p-0
                        text-center
                        align-middle
                      "
                    >

                      <span
                        className="
                          text-[8px]
                          font-semibold
                          leading-[10px]
                          text-white
                        "
                      >
                        {
                          value
                        }
                      </span>

                    </td>
                  );

                },
              )}


              {/* ==============================================
                  FINAL FOOTER CELL

                  THIS IS DIRECTLY UNDER 30-DAY TOTAL.
              ============================================== */}

              <td
                className="
                  sticky
                  right-0
                  z-40
                  h-[34px]
                  bg-[#0B2852]
                  p-0
                  text-center
                  align-middle
                  shadow-[-2px_0_4px_rgba(16,24,40,0.12)]
                "
              >

                <span
                  className="
                    text-[8px]
                    font-semibold
                    leading-[10px]
                    text-white
                  "
                >
                  {
                    footerTotal
                  }
                </span>

              </td>

            </tr>

          </tfoot>

        </table>

      </div>

    </div>
  );
};


export default TeamActiveHoursTable;